import { css } from '@emotion/react';
import { colors } from '@/styles';
import OLogo from '@/assets/OLogo_Transparent.png';
import { Grid, Checkbox } from '@material-ui/core';
import { OptionRow, Circle, SubTitle, Label } from './blocks';
import { useClientUser } from '@/hooks/useClientUser';

const dollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

export const MarketplaceOption = ({
    override_attr,
    subtext,
    breakdown,
    overridesById,
    order,
    callbacks,
    tz,
    // When supplied, brand the row with this client's name/logo instead of the
    // generic "Onward Marketplace" badge. T3 fully-managed overrides pass none.
    client,
    ...rest
}) => {
    const { circles } = useClientUser();
    const isSelected = order[override_attr] === breakdown?._meta?.override_id;

    // The figure is final — not a marketplace auction estimate — when a carrier is
    // locked in or the account auto-assigns one on confirm. Drives the
    // "Price" vs "Estimated Price" label.
    const hasCarrier = order.carrier_id && isSelected;
    const willAutoAssign =
        circles?.['auto-assign-carrier'] ||
        (circles?.['auto-assign-by-zip'] && !circles?.['auto-assign-by-zip-passthrough']);
    const isConcretePrice = !!(hasCarrier || willAutoAssign);

    let price = [breakdown.min_charge, breakdown.max_charge]
        .map((price) => (price ? dollar.format(price) : '--'))
        .join(' - ');
    if (breakdown.max_charge === breakdown.min_charge) {
        price = breakdown.max_charge ? dollar.format(breakdown.max_charge) : '--';
    }

    const label = client?.business_name || 'Onward Marketplace';
    const logo = client?.profile_img || OLogo;
    const logoBg = client?.profile_img_color || colors.white.primary;

    const onClick = () => {
        callbacks.submitOrder({
            ...order,
            [override_attr]: breakdown?._meta?.override_id,
            middle_mile_shipper_rate_override_id: null,
            integration_reservation_id: null,
        });
    };

    const displayPrice = isConcretePrice ? `Price: ${price}` : `Estimated Price: ${price}`;

    return (
        <OptionRow selected={isSelected} className="option" {...rest}>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0;
                    flex-basis: 0;
                    justify-content: center;
                    margin-right: 8px;
                `}
                onClick={onClick}
            >
                <Checkbox
                    checked={isSelected}
                    disabled={isSelected}
                    css={css`
                        &.Mui-checked {
                            color: ${colors.greens.primary};
                        }
                    `}
                />
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0;
                    flex-basis: 0;
                    justify-content: center;
                    margin-right: 20px;
                `}
            >
                <Circle
                    css={css`
                        background: ${logoBg};

                        display: flex;
                        justify-content: center;
                        align-items: center;
                    `}
                >
                    <img
                        alt={client?.business_name || 'Onward'}
                        css={css`
                            object-fit: contain;
                            display: flex;
                            height: 35px;
                            width: 35px;
                        `}
                        src={logo}
                    />
                </Circle>
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 1;
                    flex-basis: 0;
                    justify-content: center;
                `}
            >
                <Grid
                    container
                    direction="row"
                    css={css`
                        margin-bottom: 4px;
                    `}
                    onClick={onClick}
                >
                    <Label
                        css={css`
                            color: #2b2b2b;
                        `}
                    >
                        {label}
                    </Label>
                </Grid>
                <Grid container direction="row" onClick={onClick}>
                    <SubTitle
                        css={css`
                            color: #2b2b2b;
                        `}
                    >
                        {subtext}
                    </SubTitle>
                </Grid>
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0;
                    flex-basis: 0;
                    justify-content: center;
                `}
                onClick={onClick}
            >
                <Label
                    css={css`
                        color: #2b2b2b;
                        white-space: nowrap;
                    `}
                >
                    {displayPrice}
                </Label>
            </Grid>
        </OptionRow>
    );
};
